import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { Map } from 'immutable';
import classNames from 'classnames';

import { UserInput } from '../UserInput';
import { IconButton } from '../../helpers/IconButton';
import copyToClipboard from '../../../utils/copyToClipboard';
import style from './style.module.css';

const COPIED_TIMEOUT = 2000;

/**
 * Fireflies API token input with the webhook URL from the API that can be copied to the clipboard.
 *
 * @param props.compact {Boolean} Show only the copy icon next to the input instead of the webhook URL.
 * @param props.formMode {String} Form mode (view|edit).
 * @param props.formError {Immutable.Map} Error on form.
 * @param props.selectedUser {Immutable.Map} Selected user.
 */
const FirefliesApiKeyComponent = ({ compact = false, formMode, formError, selectedUser }) => {
  const [isCopied, setIsCopied] = useState(false);
  const webhookUrl = selectedUser.get('firefliesWebhookUrl');

  useEffect(() => {
    if (!isCopied) return undefined;

    const timeoutId = setTimeout(() => setIsCopied(false), COPIED_TIMEOUT);

    return () => clearTimeout(timeoutId);
  }, [isCopied]);

  const handleCopyClick = useCallback(() => {
    copyToClipboard(webhookUrl);
    setIsCopied(true);
  }, [webhookUrl]);

  return (
    <div className={classNames(style.host, { [style.compact]: compact })}>
      <UserInput
        formError={formError}
        formMode={formMode}
        label="Fireflies API Key"
        name="firefliesApiToken"
        selectedUser={selectedUser}
      />
      {webhookUrl && (
        <div className={style.webhook}>
          {!compact && (
            <span className={style.url} title={webhookUrl}>
              {webhookUrl}
            </span>
          )}
          <IconButton
            icon={isCopied ? 'check' : 'clipboard'}
            onClick={handleCopyClick}
            size="sm"
            title={isCopied ? 'Copied' : 'Copy webhook URL'}
            variant={isCopied ? 'success' : 'default'}
          />
        </div>
      )}
    </div>
  );
};

FirefliesApiKeyComponent.propTypes = {
  compact: PropTypes.bool,
  formError: PropTypes.instanceOf(Map).isRequired,
  formMode: PropTypes.string.isRequired,
  selectedUser: PropTypes.instanceOf(Map).isRequired,
};

export const FirefliesApiKey = memo(FirefliesApiKeyComponent);
